/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useEffect, useState, useRef } from 'react';
import {
  View,
  Modal,
  Carousel,
  type CarouselInstanceRef,
  ChevronLeft,
  ChevronRight,
  Icon,
  Divider,
} from '@az/starc-ui';
import styles from './styles.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ModalThumbnails } from '../Thumbnails/ModalThumbnails';
import { type ReviewType } from '../../../interface/types';
import { type ReviewPhotoMap } from '@/features/customReviews/hooks/useProductReviewImages';
import NextImage from '@/components/NextImage';

type ReviewImagesModalProps = {
  isReviewDetailsModalOpen: boolean;
  handleReviewDetailsModalOpen: (stateValue: boolean) => void;
  images: string[] | undefined;
  photoCaptions: string[] | undefined | null;
  imageIndex: number;
  setImageIndex: (stateValue: number) => void;
  reviews: ReviewType[] | undefined | null | ReviewType;
  photoMap?: ReviewPhotoMap | undefined | null;
  renderReview?: (reviewData: ReviewType, detailsModal: boolean) => JSX.Element;
};

const getCurrentReview = (
  imageUrl: string | undefined,
  reviews: ReviewType | ReviewType[] | null | undefined,
  photoMap?: ReviewPhotoMap | null | undefined
): ReviewType | null | undefined => {
  if (!imageUrl || !reviews) {
    return undefined;
  }

  if (!photoMap || !Array.isArray(reviews)) {
    return Array.isArray(reviews) ? reviews[0] : reviews;
  }

  for (const reviewId in photoMap) {
    if (photoMap[reviewId]?.includes(imageUrl)) {
      const foundReview = reviews.find((review) => review.reviewId === reviewId);
      return foundReview;
    }
  }

  return Array.isArray(reviews) ? reviews[0] : reviews;
};

export const ReviewImageDetailsModal: React.FC<ReviewImagesModalProps> = ({
  isReviewDetailsModalOpen,
  handleReviewDetailsModalOpen,
  images,
  photoCaptions,
  imageIndex,
  setImageIndex,
  reviews,
  photoMap,
  renderReview,
}) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<CarouselInstanceRef>();
  const [currentReview, setCurrentReview] = useState<ReviewType | undefined | null>(undefined);
  useEffect(() => {
    const review = getCurrentReview(images?.[imageIndex], reviews, photoMap);
    setCurrentReview(review);
  }, [imageIndex, images, photoMap, reviews]);

  const handleNextImage = () => {
    if (images && imageIndex < images.length - 1) {
      carouselRef.current?.navigateForward();
      setImageIndex(imageIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (imageIndex > 0) {
      carouselRef.current?.navigateBack();
      setImageIndex(imageIndex - 1);
    }
  };
  return (
    <Modal
      open={isReviewDetailsModalOpen}
      onClose={() => {
        handleReviewDetailsModalOpen(false);
        setImageIndex(0);
      }}
      className={styles.reviewImageModal}
      size={{ s: 'full', m: 'full', l: 'large', xl: 'large' }}
      hasCloseButton={true}
      headerColor={isDesktop ? 'secondary' : 'primary'}
    >
      <View className={styles.modalContentContainer} direction={'row'}>
        <View className={styles.modalImagesSection} direction={'row'}>
          <View className={styles.modalSelectedImageContainer}>
            <View className={styles.modalSelectedImage} direction={'row'}>
              <Carousel controlRef={carouselRef} hideControls>
                <button
                  onClick={handlePreviousImage}
                  className={styles.chevron}
                  aria-label="Previous image"
                  disabled={imageIndex === 0}
                >
                  <Icon svg={ChevronLeft} size={6} />
                </button>
                <NextImage
                  alt={photoCaptions?.[imageIndex] || 'Review Image' + `${imageIndex + 1}`}
                  src={images?.[imageIndex] as string}
                  width={473}
                  height={459}
                  className={styles.modalSelectedImage}
                />
                <button
                  onClick={handleNextImage}
                  className={styles.chevron}
                  aria-label="Next image"
                  disabled={!images || imageIndex === images.length - 1}
                >
                  <Icon svg={ChevronRight} size={6} />
                </button>
              </Carousel>
            </View>
          </View>

          <View className={styles.imagesCarousel}>
            <ModalThumbnails
              images={images}
              imageIndex={imageIndex}
              setImageIndex={setImageIndex}
              containerRef={containerRef}
              data-testid="modal-thumbnails"
              photoCaptions={photoCaptions}
            />
          </View>
        </View>
        <Divider className={styles.customerInfoSeparator} />
        <View>
          <View className={styles.reviewContent}>
            {renderReview && currentReview && renderReview(currentReview, true)}
          </View>
        </View>
      </View>
    </Modal>
  );
};
